import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Loader2, AlertCircle, Upload, FileText, Mail, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import type { WsStatus } from '../types';
import { getThemeAccents, type ClaimTheme } from '../constants';
import { cn } from '@/lib/utils';

interface WelcomeSectionProps {
  onFileUpload: (files: File[]) => void;
  onUseEmailForwarding: () => void;
  wsStatus: WsStatus;
  reconnectAttempt?: number;
  onRetry?: () => void;
  theme?: ClaimTheme;
  // Email forwarding state (when expanded)
  emailForwardingState?: {
    isLoading: boolean;
    emailAddress: string | null;
    error: string | null;
  };
}

export function WelcomeSection({ onFileUpload, onUseEmailForwarding, wsStatus, reconnectAttempt = 0, onRetry, emailForwardingState, theme = 'invol' }: WelcomeSectionProps) {
  const themeAccents = getThemeAccents(theme);
  const [copied, setCopied] = useState(false);
  const isConnected = wsStatus === 'connected';
  const isConnecting = wsStatus === 'connecting';
  const isReconnecting = wsStatus === 'reconnecting';

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onFileUpload(acceptedFiles);
      }
    },
    [onFileUpload]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: 32 * 1024 * 1024, // 32 MB
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/webp': ['.webp'],
    },
    disabled: !isConnected,
    multiple: false,
    noClick: false,
  });

  return (
    <motion.div
      key="welcome-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
      className="flex flex-col items-center justify-center min-h-[70vh] max-w-lg mx-auto px-4"
    >
      {/* Header */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.4 }}
        className="text-center mb-8"
      >
        <div className={cn(
          'w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center shadow-lg',
          themeAccents.iconBg,
          themeAccents.buttonShadow
        )}>
          <Sparkles className={cn('w-8 h-8', themeAccents.iconColor)} />
        </div>
        <h1 className="text-4xl font-bold text-slate-900 tracking-tight mb-3">
          Starten Sie Ihren Anspruch
        </h1>
        <p className="text-lg text-slate-500">
          Laden Sie Ihre Buchungsbestätigung hoch und wir extrahieren automatisch die Flugdetails.
        </p>
      </motion.div>

      {/* Connection status */}
      {!isConnected && (
        <motion.div
          className="mb-6 flex items-center gap-2 text-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {isReconnecting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin text-amber-500" />
              <span className="text-amber-600">
                Verbindung wird wiederhergestellt... {reconnectAttempt > 0 && `(Versuch ${reconnectAttempt})`}
              </span>
            </>
          ) : isConnecting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin text-amber-500" />
              <span className="text-amber-600">Verbindung wird hergestellt...</span>
            </>
          ) : (
            <>
              <AlertCircle className="h-4 w-4 text-red-500" />
              <span className="text-red-600">
                Verbindung verloren.{' '}
                {onRetry ? (
                  <button
                    onClick={onRetry}
                    className="underline hover:no-underline font-medium"
                  >
                    Erneut versuchen
                  </button>
                ) : (
                  'Bitte laden Sie die Seite neu.'
                )}
              </span>
            </>
          )}
        </motion.div>
      )}

      {/* Drop zone */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        {...getRootProps()}
        className={cn(
          'w-full cursor-pointer transition-all duration-200 rounded-2xl',
          isDragActive && 'scale-[1.02]',
          !isConnected && 'cursor-not-allowed opacity-50'
        )}
      >
        <input {...getInputProps()} />

        {isDragActive ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={cn(
              'p-12 border-2 border-dashed rounded-2xl text-center',
              themeAccents.border,
              themeAccents.bgLight
            )}
          >
            <Upload className={cn('h-12 w-12 mx-auto mb-4', themeAccents.link)} />
            <p className={cn('font-semibold text-lg', themeAccents.link)}>Legen Sie Ihre Buchungsbestätigung hier ab</p>
          </motion.div>
        ) : (
          <div
            className={cn(
              'relative rounded-2xl border-2 border-dashed p-12 transition-all duration-200',
              'border-slate-200',
              theme === 'drboese'
                ? 'hover:border-yellow-300 hover:bg-yellow-50/30'
                : 'hover:border-blue-300 hover:bg-blue-50/30'
            )}
          >
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center">
                <FileText className="h-8 w-8 text-slate-400" />
              </div>
              <div>
                <p className="text-base font-medium text-slate-700 mb-1">
                  Legen Sie Ihre Buchungsbestätigung hier ab
                </p>
                <p className="text-sm text-slate-400">
                  oder <span className={cn(themeAccents.link, themeAccents.linkHover)}>Dateien durchsuchen</span>
                </p>
              </div>
              <p className="text-xs text-slate-400">
                PDF oder Bild, bis zu 32 MB
              </p>
            </div>
          </div>
        )}
      </motion.div>

      {/* OR Divider */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.4 }}
        className="flex items-center gap-4 my-6 w-full"
      >
        <div className="flex-1 h-px bg-slate-200" />
        <span className="text-sm text-slate-400 font-medium">ODER</span>
        <div className="flex-1 h-px bg-slate-200" />
      </motion.div>

      {/* Email forwarding option */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.4 }}
        className={cn(
          'w-full p-6 rounded-2xl border-2 transition-all duration-200',
          emailForwardingState?.emailAddress
            ? theme === 'drboese'
              ? 'border-yellow-200 bg-yellow-50/30'
              : 'border-blue-200 bg-blue-50/30'
            : 'border-dashed border-slate-200'
        )}
      >
        <div className="flex items-start gap-4">
          <div className={cn(
            'w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0',
            emailForwardingState?.emailAddress
              ? theme === 'drboese' ? 'bg-yellow-100' : 'bg-blue-100'
              : 'bg-slate-100'
          )}>
            <Mail className={cn(
              'h-7 w-7',
              emailForwardingState?.emailAddress ? themeAccents.link : 'text-slate-400'
            )} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-700">Leiten Sie Ihre Buchungs-E-Mail weiter</p>

            {emailForwardingState?.emailAddress ? (
              <div className="mt-3">
                <p className="text-xs text-slate-500 mb-2">Weiterleiten an:</p>
                <div className="flex items-center gap-2">
                  <code className={cn(
                    'flex-1 text-sm font-mono bg-white px-3 py-2 rounded-lg border truncate',
                    themeAccents.link,
                    theme === 'drboese' ? 'border-yellow-200' : 'border-blue-200'
                  )}>
                    {emailForwardingState.emailAddress}
                  </code>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(emailForwardingState.emailAddress!);
                        setCopied(true);
                        toast.success('E-Mail-Adresse kopiert!');
                        setTimeout(() => setCopied(false), 2000);
                      } catch {
                        toast.error('Kopieren fehlgeschlagen');
                      }
                    }}
                    className={cn(
                      'flex-shrink-0 p-2 rounded-lg transition-colors',
                      theme === 'drboese'
                        ? 'bg-[rgb(255,211,52)] hover:bg-amber-400 text-black'
                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                    )}
                    title="E-Mail-Adresse kopieren"
                  >
                    {copied ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <Copy className="w-5 h-5" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Wir extrahieren automatisch Ihre Flugdetails
                </p>
              </div>
            ) : emailForwardingState?.isLoading ? (
              <div className="flex items-center gap-2 mt-2 text-sm text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>E-Mail-Adresse wird abgerufen...</span>
              </div>
            ) : (
              <p className="text-sm text-slate-400 mt-1">
                E-Mail-Adresse wird geladen...
              </p>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
